import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { JobOfferService, LandingPageService } from 'src/app/api/services';
import { HeaderHomeComponent } from 'src/app/shared/component/header-home/header-home.component';
import { TimeAgoPipe } from 'src/app/shared/pipe/time-ago.pipe';
import { JobOfferPostulantService } from 'src/app/api/services/job-offer-postulant.service';
import { APP_CONSTANTS } from 'src/app/core/constant/constants';
import {
  JobOfferBasicResult,
  Pagination,
} from 'src/app/shared/model/job-offer-result';
import { JobOfferDetails } from 'src/app/shared/model/job-offer-detail';
import { ToastService } from 'src/app/shared/service/toast.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroCheckBadge } from '@ng-icons/heroicons/outline';
import { bootstrapSuitcaseLg } from '@ng-icons/bootstrap-icons';
import { SolesCurrencyPipe } from 'src/app/shared/pipe/soles-currency.pipe';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { TokenStorageService } from 'src/app/shared/service/token-storage.service';
import { UserStatus } from 'src/app/core/enum/user-status';

@Component({
  selector: 'app-job',
  imports: [
    HeaderHomeComponent,
    CommonModule,
    DatePipe,
    TimeAgoPipe,
    NgIcon,
    SolesCurrencyPipe,
    LoadingButtonComponent,
  ],
  templateUrl: './job.component.html',
  viewProviders: [provideIcons({ heroCheckBadge, bootstrapSuitcaseLg })],
})
export class JobComponent implements OnInit {
  jobs: JobOfferBasicResult[] = [];
  jobTypes: QueryHeader[] = [];
  province: QueryHeader[] = [];
  selectedJob: JobOfferDetails | null = null;
  isLoadingPostulant: boolean = false;
  postulantExists: boolean = false;

  isLoadingJobs: boolean = false;
  isLoadingJobDetail: boolean = false;
  modalPostulant: boolean = false;

  pagination: Pagination = {
    current: 1,
    perPage: 6,
    total: 0,
    lastPage: 0,
  };
  hasMoreJobs: boolean = true;
  default_page: number = 1;

  currentFilters: any = {};

  constructor(
    private ofertaService: JobOfferService,
    private landingService: LandingPageService,
    private postulantService: JobOfferPostulantService,
    private tokenService: TokenStorageService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadFilters();
    this.loadJobs();
  }

  loadFilters(): void {
    const payload = { idDepartament: APP_CONSTANTS.APURIMAC_ID };

    this.landingService.seccionMenuFiltroIdDepartament10Get(payload).subscribe({
      next: (res) => {
        const dto = res?.dto || {};
        this.province = (dto.provinces?.data || []).map((prov: any) => ({
          label: prov.name,
          value: prov.id,
        }));
        this.jobTypes = (dto.jobTypes?.data || []).map((type: any) => ({
          label: type.description,
          value: type.id,
        }));
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.LANDING.GET_DATA_ERROR]
        );
      },
    });
  }

  loadJobs(page: number = 1) {
    this.isLoadingJobs = true;
    this.isLoadingJobDetail = true;

    const params = {
      skip: (page - 1) * this.pagination.perPage,
      quantity: this.pagination.perPage,
    };

    this.ofertaService.getBasicSearch(params).subscribe({
      next: (res) => {
        this.jobs = res?.dto.jobOffers.data || [];
        this.pagination = res?.dto.jobOffers.pagination || this.pagination;
        this.selectedJob =
          res?.dto.jobOffers.firstJobOffer || this.jobs[0] || null;
        if (this.selectedJob) {
          this.checkIfPostulantExists();
          this.isLoadingJobDetail = false;
        }
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.SEARCH.GET_RESULTS_ERROR]
        );
        this.isLoadingJobDetail = false;
      },
      complete: () => {
        this.isLoadingJobs = false;
      },
    });
  }

  selectJob(job: JobOfferBasicResult | null) {
    this.selectedJob = null;
    this.isLoadingJobDetail = true;

    if (job) {
      this.ofertaService.getJobDetailById(job.idJobOffer).subscribe({
        next: (data) => {
          this.selectedJob = data.dto.jobOffer;
          this.checkIfPostulantExists();
        },
        error: (err) => {
          this.toastService.showMessages(
            'error',
            err.error?.listMessage || [MESSAGES.JOB_OFFER.GET_ERROR]
          );
        },
        complete: () => {
          this.isLoadingJobDetail = false;
        },
      });
    }
  }

  checkIfPostulantExists() {
    this.postulantExists = false;
    const auth = this.tokenService.getAuth();
    const jobId = this.selectedJob?.idJobOffer;

    if (!auth || !jobId) {
      this.postulantExists = false;
      return;
    }

    this.postulantService.checkIfPostulantExists(jobId).subscribe({
      next: ({ dto }) => (this.postulantExists = dto.exists),
      error: () => (this.postulantExists = false),
    });
  }

  onFiltersChanged(filters: any, page: number) {
    this.currentFilters = filters;
    this.isLoadingJobs = true;
    this.selectedJob = null;

    const payload = {
      ...filters,
      skip: (page - 1) * this.pagination.perPage,
      quantity: this.pagination.perPage,
    };

    this.ofertaService.getBasicSearch(payload).subscribe({
      next: (res) => {
        this.jobs = res?.dto.jobOffers.data || [];
        this.pagination = res?.dto?.jobOffers.pagination || this.pagination;
        if (this.jobs.length > 0) {
          this.selectJob(this.jobs[0]);
        } else {
          this.selectedJob = null;
        }
      },
      error: (err) => {
        this.toastService.showMessages(
          'error',
          err.error?.listMessage || [MESSAGES.SEARCH.GET_RESULTS_ERROR]
        );
      },
      complete: () => (this.isLoadingJobs = false),
    });
  }

  postulate() {
    const idJobOffer = this.selectedJob?.idJobOffer;
    if (!idJobOffer) return;

    const auth = this.tokenService.getAuth();
    if (!auth) {
      this.toastService.showMessages('warning', [MESSAGES.AUTH.UNAUTHORIZED]);
      return;
    }

    if (auth.status == UserStatus.INCOMPLETE) {
      this.toastService.showMessages('warning', [
        MESSAGES.AUTH.PROFILE_ICOMPLETE,
      ]);
      return;
    }

    this.isLoadingPostulant = true;

    this.postulantService.postulate(idJobOffer).subscribe({
      next: (res) => {
        if (res.type === 'success') {
          this.toastService.showMessages('success', [
            MESSAGES.POSTULANT.APPLY_SUCCESS,
          ]);
          this.postulantExists = true;
        } else {
          res.listMessage.forEach((msg) =>
            this.toastService.showMessages('error', [msg])
          );
        }
        this.isLoadingPostulant = false;
        this.closeModalPostulate();
      },
      error: (err) => {
        const messages = err?.error?.listMessage;
        this.toastService.showMessages('error', [
          messages || MESSAGES.POSTULANT.APPLY_ERROR,
        ]);
        this.isLoadingPostulant = false;
        this.closeModalPostulate();
      },
    });
  }

  goToPage(page: number): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (page >= 1 && page <= this.pagination.lastPage) {
      this.loadJobs(page);
    }
  }

  nextPage(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (this.pagination.current < this.pagination.lastPage) {
      this.goToPage(this.pagination.current + 1);
    }
  }

  prevPage(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (this.pagination.current > 1) {
      this.goToPage(this.pagination.current - 1);
    }
  }

  get showingFrom(): number {
    return (this.pagination.current - 1) * this.pagination.perPage + 1;
  }

  get showingTo(): number {
    return Math.min(
      this.pagination.current * this.pagination.perPage,
      this.pagination.total
    );
  }

  get pagesToShow(): number[] {
    const total = this.pagination.lastPage;
    const current = this.pagination.current;
    const delta = 1;

    const pages: number[] = [];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    pages.push(1);

    if (left > 2) {
      pages.push(-1);
    }
    for (let i = left; i <= right; i++) {
      pages.push(i);
    }
    if (right < total - 1) {
      pages.push(-1);
    }
    if (total > 1) {
      pages.push(total);
    }

    return pages;
  }

  openModalPostulate() {
    this.modalPostulant = true;
  }

  closeModalPostulate() {
    this.modalPostulant = false;
  }

  companyImageFor(job: JobOfferBasicResult): string {
    const url = job.companyUrlBannerMini;
    if (!url || url.trim() === '') {
      return APP_CONSTANTS.DEFAULT_IMAGE_COMPANY;
    }
    return url;
  }

  companyImage(): string {
    if (!this.selectedJob) {
      return APP_CONSTANTS.DEFAULT_IMAGE_COMPANY;
    }
    const url = this.selectedJob.company.urlBannerMiniPage;
    if (!url || url.trim() === '') {
      return APP_CONSTANTS.DEFAULT_IMAGE_COMPANY;
    }
    return url;
  }

  companyBanner(): string {
    if (!this.selectedJob) {
      return APP_CONSTANTS.DEFAULT_BANNER_COMPANY;
    }
    const url = this.selectedJob.company.urlBannerMvv;
    if (!url || url.trim() === '') {
      return APP_CONSTANTS.DEFAULT_BANNER_COMPANY;
    }
    return url;
  }
}
